import { periodOfLocal } from './tz.js';
/**
 * L'état de la collecte.
 *
 * ⚠️ Le cas `pending` mérite son traitement à part : un premier démarrage n'a
 * légitimement rien collecté, et l'annoncer dégradé ferait échouer la porte de santé du
 * tout premier déploiement. Mais il ne peut pas rester indéfiniment innocent — une
 * application qui tourne depuis deux jours sans avoir jamais collecté est en panne, même
 * si aucune tâche n'a échoué (le collecteur peut n'avoir jamais démarré, et personne ne
 * le saurait). Au-delà du seuil, `pending` devient dégradé lui aussi.
 */
export function assessCollect(input) {
    const base = {
        ageHours: input.ageHours,
        thresholdHours: input.thresholdHours,
        failed: input.failed,
    };
    // Une tâche abandonnée est le signal le plus fort : elle a déjà été réessayée.
    if (input.failed > 0)
        return { ...base, state: 'failing' };
    if (input.ageHours === null) {
        return { ...base, state: 'pending' };
    }
    return { ...base, state: input.ageHours > input.thresholdHours ? 'stale' : 'ok' };
}
/** Un état de collecte suffit-il à déclarer le service dégradé ? */
export function collectIsDegraded(health, uptimeHours) {
    if (health.state === 'failing' || health.state === 'stale')
        return true;
    // `pending` : innocent tant que le service n'a pas eu le temps de collecter une fois.
    if (health.state === 'pending')
        return uptimeHours > health.thresholdHours;
    return false;
}
/**
 * La couverture, et surtout ses **trous**.
 *
 * ⚠️ `expected` est l'étendue entre le plus ancien et le plus récent mois **stockés**, pas
 * la fenêtre de rétention visée. C'est délibéré : un service fraîchement déployé n'a
 * légitimement qu'un mois, et le déclarer incomplet pour cela n'aurait aucun sens. Ce qui
 * mérite une alerte, c'est un **trou au milieu** — un mois qu'une collecte a manqué et que
 * personne ne rattrapera, puisque le planificateur ne remonte pas le temps.
 *
 * ⚠️ `knownEmpty` est ce qui distingue un trou d'un mois sans activité. La source des mois
 * stockés est la liste des **partitions**, et une partition ne naît qu'à la première ligne
 * insérée : un mois ingéré sans aucun travail n'en laisse pas, et passerait pour un trou.
 * On lui oppose donc les ingestions réussies, qui, elles, gardent trace du mois traité.
 * Ces mois n'entrent jamais dans les bornes — sans quoi une ingestion dont la partition a
 * depuis été purgée ferait réapparaître un mois disparu, et inventerait le trou qui suit.
 */
export function assessCoverage(storedMonths, knownEmpty = []) {
    const months = [...new Set(storedMonths)].sort();
    if (months.length === 0) {
        return { oldestMonth: null, newestMonth: null, months: 0, expected: 0, missing: [] };
    }
    const oldest = months[0];
    const newest = months[months.length - 1];
    const excuses = new Set(knownEmpty);
    const presents = new Set(months);
    const attendus = monthsBetween(oldest, newest);
    return {
        oldestMonth: oldest,
        newestMonth: newest,
        months: months.length,
        expected: attendus.length,
        missing: attendus.filter((m) => !presents.has(m) && !excuses.has(m)),
    };
}
/** Les mois de `from` à `to`, bornes comprises. Les deux sont au format « AAAA-MM ». */
export function monthsBetween(from, to) {
    const [y0, m0] = from.split('-').map(Number);
    const [y1, m1] = to.split('-').map(Number);
    const out = [];
    for (let y = y0, m = m0; y < y1 || (y === y1 && m <= m1);) {
        out.push(`${y}-${String(m).padStart(2, '0')}`);
        if (m === 12) {
            y += 1;
            m = 1;
        }
        else {
            m += 1;
        }
    }
    return out;
}
/**
 * Décale une période « AAAA-MM » de `n` mois (n peut être négatif).
 *
 * Sert à dire **quand** un mois sortira de la fenêtre de conservation : un mois sort quand
 * la fenêtre, qui glisse d'un mois chaque mois, ne le contient plus — soit
 * `addMonths(mois, rétention)`. Avec dix-huit mois, septembre 2025 sort en mars 2027.
 */
export function addMonths(period, n) {
    const [y, m] = period.split('-').map(Number);
    const index = y * 12 + (m - 1) + n;
    return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
}
/**
 * Le premier mois CONSERVÉ : le mois courant et les `retentionMonths − 1` précédents, le mois
 * courant étant lu **à l'heure locale**.
 *
 * 🪤 Il se calculait en UTC : le 1ᵉʳ du mois entre minuit et 1 h (2 h en été), la fenêtre
 * gardait un mois de trop. Le sens était prudent — on ne purgeait jamais trop tôt — mais
 * l'écran de conservation, qui annonce « sortira en mars 2027 », aurait eu tort pendant ces
 * heures-là. Une seule règle pour l'écran et pour la purge.
 */
export function retentionCutoff(now, retentionMonths) {
    return addMonths(periodOfLocal(now), -(retentionMonths - 1));
}
